import { FC } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { softDeleteShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderNotPaidListForSellerByShopUrl } from '@shared/api/shopOrder/shopOrder.hook';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import { Button } from '@shared/components/Button';
import DismissModal from '@shared/components/DismissModal';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { formatDate } from '@shared/utils/formatDate';
import { useQueryClient } from 'react-query';

import { ShopOrderDetailInfoCard } from './ShopOrderDetailInfoCard';

const NotPaidOrderCard: FC<{ order: ShopOrderForSeller }> = function ({ order }) {
  const deliveryDetail = order.deliveryDetail;
  const shopItem = order.orderDetail.shopItem;

  const deleteOpenId = `notPaidOrderDelete-${order._id}`;
  const setDeleteOpen = useSetRecoilState(simpleOpenAtomFamily(deleteOpenId));
  const queryClient = useQueryClient();
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const { sellerRole } = useAuth();

  //* 가상계좌(미입금) 주문 소프트 삭제 — 문서는 남고 목록에서만 빠진다.
  //* 되살리기는 페이지 하단 "삭제한 내역 검색"에서 주문자 번호로.
  //* 주의: 삭제 후에도 토스 웹훅은 입금을 받아 Paid로 바꾼다(docStatus는 안 건드림) →
  //* 지운 뒤 입금되면 발송목록에 안 뜨므로, 삭제한 내역 검색에서 복원해야 한다.
  //* 결제 시 서버가 docStatus를 자동 복원하도록 요청해둔 상태(docs/shop-order-waiting-delete-spec.md 2차).
  //* 반영 뒤에도 구서버·기존 데이터 때문에 이 안내와 삭제내역 경고는 유지한다.
  const onDeleteClick = async () => {
    try {
      await softDeleteShopOrderForSeller(order._id, sellerRole());
      await queryClient.invalidateQueries(
        shopOrderNotPaidListForSellerByShopUrl(sellerRole()),
      );
      notificateSuccess(notifiacationInstance, '주문 삭제 완료');
    } catch (err: any) {
      console.error(err);
      const status = err?.response?.status;
      notificateError(
        notifiacationInstance,
        status === 400
          ? err?.response?.data?.message ?? '삭제할 수 없는 주문입니다.'
          : status === 404
          ? '아직 서버에 반영되지 않은 기능입니다. 관리자에게 문의해주세요.'
          : status === 401 || status === 403
          ? '삭제 권한이 없습니다. 관리자에게 문의해주세요.'
          : '주문 삭제에 실패했습니다.',
      );
    }
  };

  return (
    <div className="space-y-2">
      <div className="h-auto w-full overflow-hidden rounded-lg border shadow">
        <div className="p-5 pb-3">
          <dl>
            <dt className="truncate text-lg font-medium text-gray-900">
              {shopItem.name}
            </dt>
            {/* 삭제 버튼은 가격 줄 오른쪽에 얇게 — 카드 높이를 늘리지 않는다 */}
            <dt className="flex items-center justify-between gap-2 text-base font-medium text-gray-900">
              <span className="truncate">
                {shopItem.priceRetail.toLocaleString()}원
              </span>
              <Button
                className="h-6 shrink-0 border-red-300 px-2 py-0 text-xs font-medium text-red-600 shadow-none hover:bg-red-50"
                onClick={() => {
                  setDeleteOpen(true);
                }}
              >
                삭제
              </Button>
            </dt>
            <dd className="space-y-0.5">
              <p className="whitespace-pre-wrap text-sm text-gray-700">
                {deliveryDetail.receiverAddress}
              </p>
              <p className="text-sm text-gray-700">
                결제시도일:{' '}
                {formatDate(order.paymentDetail.requestedAt, {
                  dateSeparater: '.',
                  contains: {
                    year: false,
                    day: true,
                    hours: true,
                    minutes: true,
                  },
                })}
              </p>
            </dd>
          </dl>
          <DismissModal
            openId={deleteOpenId}
            buttonTitle="삭제"
            title="주문내역 삭제"
            subtitle={`가상계좌 입금대기 목록에서 이 주문을 치웁니다.\n삭제 후에 입금이 들어오면 발송목록에 뜨지 않으니, 아래 "삭제한 내역 검색"에서 주문자 번호로 찾아 복원해주세요.`}
            onClick={onDeleteClick}
          ></DismissModal>
        </div>
        <div className="space-y-2 bg-gray-50 px-5 py-2">
          <p>주문번호 : {order._id}</p>
          <ShopOrderDetailInfoCard shopOrder={order} />
        </div>
      </div>
    </div>
  );
};

export { NotPaidOrderCard };
